import { useOrganizationStore } from '~/store/organization';
import { useHubStore } from '~/store/hub';
import {
  createError,
  reloadNuxtApp,
  useRuntimeConfig,
  showError,
} from '#imports';
import { useAuthUserStore } from '~/store/auth-user';
import { useAccountStore } from '~/store/account';
import { getErrorStatus, logError } from '~/api';
import { captureHandledError } from '~/constants/sentry';

const isAuthFailure = (err: unknown): boolean => {
  const status = getErrorStatus(err);
  return status === 401 || status === 403;
};

export default defineNuxtRouteMiddleware(async (to) => {
  const error = useError();
  const config = useRuntimeConfig();

  if (config.public.isMaintenanceEnabled) {
    return;
  }

  const authUserStore = useAuthUserStore();
  const { isLoggedIn } = storeToRefs(authUserStore);
  const organizationStore = useOrganizationStore();
  const { organization } = storeToRefs(organizationStore);
  const accountStore = useAccountStore();
  const hubStore = useHubStore();
  const { hub } = storeToRefs(hubStore);

  const organizationSlug = to.params.organizationSlug as string | undefined;
  const hubSlug = to.params.hubSlug as string | undefined;

  await Promise.all([
    authUserStore.initializeAuthUser(),
    organizationStore.initializeOrganization(organizationSlug),
  ]);

  if (error.value) {
    return;
  }

  const promises: Promise<void>[] = [hubStore.initializeHub(hubSlug)];

  if (isLoggedIn.value && organization.value !== null) {
    promises.push(
      accountStore.ensureCurrentAccount({
        organizationId: organization.value.id,
        onBackgroundFailure: async (reason) => {
          if (isAuthFailure(reason)) {
            await authUserStore.clearSession();
            reloadNuxtApp({ persistState: false });
            return;
          }
          logError(reason);
          captureHandledError(reason, { feature: 'account-refresh' });
        },
      }),
    );
  }

  const [hubResult, accountResult] = await Promise.allSettled(promises);

  if (accountResult?.status === 'rejected') {
    if (!isAuthFailure(accountResult.reason)) {
      throw createError({
        statusCode: getErrorStatus(accountResult.reason) ?? 500,
        fatal: true,
        cause: accountResult.reason,
      });
    }
    authUserStore.clearSession();
  }

  if (hubResult.status === 'rejected') {
    const status = getErrorStatus(hubResult.reason);

    if (status === 403) {
      const redirect =
        to.path === '/auth/login'
          ? undefined
          : `/auth/login?redirect=${encodeURIComponent(to.fullPath)}`;

      return navigateTo(redirect);
    }

    if (status === 404 || status === 400) {
      return showError({ statusCode: 404 });
    }

    throw hubResult.reason;
  }
});
